<template lang="pug">
  NewsletterSubscriptionForm.newsletter-subscription-form(
    v-model="value"
    :placeholder="PLACEHOLDER"
    :submitButtonText="SUBMIT_BUTTON_TEXT"
    :feedbackTitle="feedback.title"
    :feedback="feedback.subtitle"
    :img="image_url || defaultImageUrl"
    :state="state"
    :contentWrapperColor="$options.text[type].contentWrapperColor"
    :btnColor="$options.text[type].btnInputColor"
    :inputBorderActiveColor="$options.text[type].inputBorderActiveColor"
    :textColor="$options.text[type].textColor"
    :location="location"
    :style="cssStyles"
    @form-submit="onSubmit"
    @update:modelValue="onUpdateValue",
  )
    template(#title)
      TheContent(v-for="item of titleWithFallback" :key="item.id" :elem="item")

    template(#subtitle)
      TheContent(v-for="item of subtitleWithFallback" :key="item.id" :elem="item")

</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import {
    FORM_DATA,
    successFeedback,
    failureFeedback,
    PLACEHOLDER,
    SUBMIT_BUTTON_TEXT,
    type FormType,
    type FormState,
  } from './NewsletterSubscriptionFormConfig';
  import TheContent from '~/components/content/TheContent';
  import { isArticlePage, isHomePage, isTopPage } from '~/utils/router';
  import NewsletterSubscriptionForm from '~/components/lh-ui/NewsletterSubscriptionForm/NewsletterSubscriptionForm.vue';

  export default defineNuxtComponent({
    text: FORM_DATA,

    name: 'NewsletterSubscriptionFormContainer',

    components: {
      NewsletterSubscriptionForm,
      TheContent,
    },

    props: {
      type: {
        type: String || (null as unknown as PropType<FormType | null>),
        required: true,
      },
      action: {
        type: String as PropType<string>,
        required: true,
      },
      location: {
        type: String as PropType<string>,
        default: '',
      },

      image_url: {
        type: String as PropType<string>,
        default: undefined,
      },
      title: {
        type: Array || (String as PropType<TContent[] | string>),
        default: undefined,
      },
      subtitle: {
        type: Array || (String as PropType<TContent[] | string>),
        default: undefined,
      },
    },

    data() {
      return {
        value: '' as string,
        state: 'initial' as FormState,
        gaStartEventSend: false as boolean,
        PLACEHOLDER,
        SUBMIT_BUTTON_TEXT,
      };
    },

    computed: {
      getTypePage(): string {
        const { name } = this.$route;

        switch (true) {
          case isTopPage(name):
            return 'Лучшее';
          case isHomePage(name):
            return 'Главная';
          case isArticlePage(name):
            return 'Внутренние';
          default:
            return '';
        }
      },

      // Гутенберге приходит из пропсов, легаси для виджетов
      titleWithFallback(): TContent[] {
        if (Array.isArray(this.title)) {
          return this.title;
        }

        return [
          {
            id: Number(Math.random().toString(36).substr(2)),
            parser_type: 'html_parser',
            text: '',
            type: 'p',
            attributes: [],
            children: [
              {
                id: Number(Math.random().toString(36).substr(2)),
                parser_type: 'html_parser',
                text: this.title ?? FORM_DATA[this.type as FormType].title,
                type: '#text',
                attributes: [],
                children: [],
              },
            ],
          },
        ];
      },

      // Гутенберге приходит из пропсов, легаси для виджетов
      subtitleWithFallback(): TContent[] {
        if (Array.isArray(this.subtitle)) {
          return this.subtitle;
        }

        return [
          {
            id: Number(Math.random().toString(36).substr(2)),
            parser_type: 'html_parser',
            text: '',
            type: 'p',
            attributes: [],
            children: [
              {
                id: Number(Math.random().toString(36).substr(2)),
                parser_type: 'html_parser',
                text: this.subtitle ?? 'Дарим электронную книгу за подписку',
                type: '#text',
                attributes: [],
                children: [],
              },
            ],
          },
        ];
      },

      // Гутенберге приходит из пропсов, легаси для виджетов
      defaultImageUrl(): string {
        return `/wp-content/plugins/lh-gutenberg-blocks/blocks/EmailBlock/assets/images/${this.type}.jpg`;
      },

      formTitleForAnalytics(): string {
        return this.titleWithFallback[0]?.children?.[0]?.text ?? '';
      },

      cssStyles(): Record<string, string> {
        return {
          '--mb-form': isHomePage(this.$route.name) ? '0' : '24px',
        };
      },
      feedback(): { title: string; subtitle: string } {
        return this.state === 'success' ? successFeedback : failureFeedback;
      },
    },

    methods: {
      async onSubmit(): Promise<void> {
        try {
          this.setState('sending');
          const response = await this.$wordpressApi.subscribeToNewsletter(
            this.action,
            this.type,
            this.value,
          );
          if (response.status === 200 && response.data.success) {
            this.setState('success');
            this.value = '';
          } else {
            this.handleSubscribeToNewsletterError();
          }
        } catch {
          this.handleSubscribeToNewsletterError();
        }
      },
      handleSubscribeToNewsletterError(): void {
        this.setState('failure');
      },
      onUpdateValue(): void {
        if (this.gaStartEventSend) {
          return;
        }

        this.gaStartEventSend = true;
      },
      setState(state: FormState): void {
        window.requestAnimationFrame(() => {
          this.state = state;
        });
      },
    },
  });
</script>

<style lang="scss" scoped>
  $mbForm: var(--mb-form);
  .newsletter-subscription-form {
    margin: $mbForm 0;

    @include widget-above-comments {
      background: white;
    }
  }
</style>
